import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { Box, Button, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import MUITooltip, { tooltipClasses } from '@mui/material/Tooltip';

import { storeTooltipData } from './util';
import tooltipContent from './tooltipContent';
import { colors } from '../../../theme/color-palette';
import { BOGLE_LIGHT, BOGLE_MEDIUM } from '../../../constants/constant';

const { TOOLTIP, PURPLE, WHITE } = colors;

const useStyles = makeStyles(() => ({
	title: {
		fontSize: '18px',
		fontFamily: BOGLE_MEDIUM,
		color: WHITE,
		fontWeight: 500,
	},
	content: {
		fontSize: '16px',
		marginTop: '16px',
		fontFamily: BOGLE_LIGHT,
		color: WHITE,
		fontWeight: 300,
		lineHeight: '20px',
	},
	button: {
		backgroundColor: WHITE,
		color: PURPLE,
		textTransform: 'none',
		fontSize: '14px',
		fontFamily: BOGLE_MEDIUM,
		width: '68px',
		height: '35px',
		'&:hover': {
			backgroundColor: WHITE,
		},
	},
}));

const tooltipStyles = {
	maxWidth: '328px',
	maxHeight: '160px',
	margin: 0,
	padding: '24px 16px 16px 16px',
};

const StyledTooltip = styled(({ className, ...props }) => (
	<MUITooltip {...props} classes={{ tooltip: className }} />
))(() => ({
	[`& .${tooltipClasses.arrow}`]: {
		color: TOOLTIP.BG,
	},
	backgroundColor: TOOLTIP.BG,
	borderRadius: '8px',
}));

const AddAlertTooltip = (props) => {
	const classes = useStyles();
	return (
		<StyledTooltip
			sx={tooltipStyles}
			title={
				<React.Fragment>
					<Typography className={classes.title}>Add alerts</Typography>
					<Typography className={classes.content}>
						Add alerts to the most important metrics to track the performance
						for various time frames. Alert notifications will be sent via e-mail
						or in-app notification
					</Typography>
				</React.Fragment>
			}
			arrow
			placement="left-start">
			{props.children}
		</StyledTooltip>
	);
};

AddAlertTooltip.propTypes = {
	children: PropTypes.node,
};

/**
 * Custom Tooltip w/ title, content and button
 */
const Tooltip = ({ type, show, children, ...rest }) => {
	const classes = useStyles();
	const content = tooltipContent[type];
	const [showTooltip, setShowTooltip] = useState(false);

	useEffect(() => {
		const timeoutId = setTimeout(() => setShowTooltip(show), 500);
		return () => clearTimeout(timeoutId);
	}, [show]);

	const buttonClickHandler = () => {
		setShowTooltip(!showTooltip);
		storeTooltipData(type);
	};

	return (
		<StyledTooltip
			open={showTooltip}
			sx={tooltipStyles}
			title={
				<Box>
					<Typography className={classes.title}>{content['title']}</Typography>
					<Typography className={classes.content}>{content['desc']}</Typography>
					<Box
						sx={{
							marginTop: type === 'metric' ? '5px' : '28px',
							display: 'flex',
							justifyContent: 'flex-end',
						}}>
						<Button className={classes.button} onClick={buttonClickHandler}>
							Got it
						</Button>
					</Box>
				</Box>
			}
			arrow
			{...rest}>
			{children}
		</StyledTooltip>
	);
};

Tooltip.propTypes = {
	type: PropTypes.string,
	show: PropTypes.bool,
	children: PropTypes.node,
};

export { AddAlertTooltip, Tooltip };
